/**
 * Options for resolving locales when RTL locales are behind a rollout flag.
 */
export type MapLocaleToSupportedOptions = {
  /**
   * Defaults to `false`. Pass `true` only in entry points that have evaluated
   * the RTL locale rollout flag.
   */
  isRtlLocaleFeatureEnabled?: boolean;
};

/**
 * Canonical locale values supported by the application.
 *
 * These enum values are the single source of truth for locale-specific
 * routing, translation loading, and locale-aware formatting helpers.
 */
export enum SupportedLocale {
  En = 'en',
  /** ICU pseudolocalisation of the English catalog (`en/messages.json`); dev/QA only. Uses `en-psaccent` so it does not collide with real `en-PS` (Palestinian Territories). Next.js `i18n.locales` omits this in production — see `next.config.js`. */
  EnPs = 'en-psaccent',
  EsEs = 'es-ES',
  DeDe = 'de-DE',
  FrFr = 'fr-FR',
  ItIt = 'it-IT',
  JaJp = 'ja-JP',
  KoKr = 'ko-KR',
  ZhCn = 'zh-CN',
  ZhTw = 'zh-TW',
  CaEs = 'ca-ES',
  DaDk = 'da-DK',
  NlNl = 'nl-NL',
  FiFi = 'fi-FI',
  NbNo = 'nb-NO',
  PlPl = 'pl-PL',
  PtPt = 'pt-PT',
  SvSe = 'sv-SE',
  ArSa = 'ar-SA',
}

/**
 * Ordered list of supported locale codes.
 *
 * Keep this array derived from `SupportedLocale` so consumers can iterate over
 * every locale without maintaining a second source of truth.
 */
export const supportedLocales = Object.values(SupportedLocale);

/**
 * Locales offered to {@link getSupportedLocaleFromHeader} / negotiators.
 * Excludes {@link SupportedLocale.EnPs} so `Accept-Language` never resolves
 * to the pseudolocale.
 */
export const getSupportedLocalesForLanguageNegotiation = (
  options?: MapLocaleToSupportedOptions
): SupportedLocale[] =>
  supportedLocales.filter(
    (locale) =>
      locale !== SupportedLocale.EnPs &&
      withoutRtlLocaleWhenDisabled(locale, options?.isRtlLocaleFeatureEnabled) !==
        undefined
  );

/**
 * Fallback locale used whenever the input is missing, invalid, or unsupported.
 */
export const DEFAULT_LOCALE: SupportedLocale = SupportedLocale.En;

/** Locales where family (surname) name precedes given name in display. */
export const CJK_LOCALES = new Set<SupportedLocale>([
  SupportedLocale.JaJp,
  SupportedLocale.KoKr,
  SupportedLocale.ZhCn,
  SupportedLocale.ZhTw,
]);

export const isCjkLocale = (locale: SupportedLocale): boolean => CJK_LOCALES.has(locale);

const supportedLocalesSet = new Set<string>(supportedLocales);

const RTL_SUPPORTED_LOCALES = new Set<SupportedLocale>([SupportedLocale.ArSa]);

const DEFAULT_RTL_LOCALE_FEATURE_ENABLED = false;

const withoutRtlLocaleWhenDisabled = (
  resolved: SupportedLocale | undefined,
  isRtlLocaleFeatureEnabled = DEFAULT_RTL_LOCALE_FEATURE_ENABLED
): SupportedLocale | undefined => {
  if (resolved && RTL_SUPPORTED_LOCALES.has(resolved) && !isRtlLocaleFeatureEnabled) {
    return undefined;
  }
  return resolved;
};

/** Matches a leading locale segment, including the dev-only `en-psaccent` pseudolocale (longer than `xx-YY`). */
const LOCALE_PATH_PREFIX_REGEX =
  /^\/(?:en-psaccent|[A-Za-z]{2}(?:-[A-Za-z]{2})?)(?=\/|$)/i;

/**
 * Builds a map from ISO 639 language subtag → locale string for shorthand
 * resolution (e.g. `fr` → `fr-FR`). If two entries share the same language
 * subtag, the **first** in `locales` wins.
 */
export function buildLocaleAliasMap<T extends string>(
  locales: readonly T[]
): Record<string, T> {
  const map = {} as Record<string, T>;
  for (const loc of locales) {
    const lang = new Intl.Locale(loc).language.toLowerCase();
    if (map[lang] === undefined) {
      map[lang] = loc;
    }
  }
  return map;
}

const localeAliasMap = buildLocaleAliasMap(supportedLocales);

type NormalizedLocaleInput = {
  normalizedLocale: string;
  languageAlias?: string;
};

const trimLocaleInput = (locale: string): string => locale.trim().replaceAll('_', '-');

/**
 * Normalizes already-trimmed locale strings via `Intl.Locale`.
 *
 * Callers that have not trimmed should use {@link trimLocaleInput} first.
 * Invalid values return `normalizedLocale` as the input string and omit
 * `languageAlias` when parsing throws.
 */
const normalizeTrimmedLocaleInput = (trimmedLocale: string): NormalizedLocaleInput => {
  if (!trimmedLocale) {
    return { normalizedLocale: '' };
  }

  try {
    const parsedLocale = new Intl.Locale(trimmedLocale);
    return {
      normalizedLocale: parsedLocale.toString(),
      languageAlias: parsedLocale.language.toLowerCase(),
    };
  } catch {
    return { normalizedLocale: trimmedLocale };
  }
};

/**
 * Resolves a user-provided locale string to a supported locale.
 *
 * The lookup accepts:
 * - exact supported locales such as `en` or `fr-FR`
 * - common aliases that share the same language subtag, such as `fr` -> `fr-FR`
 * - loosely formatted input like `pt_PT`, which is normalized before matching
 *
 * Returns `undefined` when the value is empty or cannot be mapped.
 */
export const mapLocaleAliasToSupportedLocale = (
  locale: string | null | undefined,
  options?: MapLocaleToSupportedOptions
): SupportedLocale | undefined => {
  const isRtlLocaleFeatureEnabled =
    options?.isRtlLocaleFeatureEnabled ?? DEFAULT_RTL_LOCALE_FEATURE_ENABLED;

  if (!locale) {
    return undefined;
  }

  const trimmedLocale = trimLocaleInput(locale);
  if (!trimmedLocale) {
    return undefined;
  }

  // Hot path: router and config often pass an exact supported code — skip Intl.
  if (supportedLocalesSet.has(trimmedLocale)) {
    return withoutRtlLocaleWhenDisabled(
      trimmedLocale as SupportedLocale,
      isRtlLocaleFeatureEnabled
    );
  }

  // Norwegian Bokmål: canonical locale is nb-NO. Legacy no-NO and subtags no/nb resolve here.
  const normalizedNorwegianInput = trimmedLocale.toLowerCase();
  if (
    normalizedNorwegianInput === 'no-no' ||
    normalizedNorwegianInput === 'no' ||
    normalizedNorwegianInput === 'nb'
  ) {
    return SupportedLocale.NbNo;
  }

  // Traditional Chinese (e.g. zh-Hant, zh-TW, zh-HK) → app locale zh-TW
  if (trimmedLocale.toLowerCase().startsWith('zh')) {
    try {
      const parsed = new Intl.Locale(trimmedLocale);
      if (parsed.language === 'zh' && parsed.maximize().script === 'Hant') {
        return SupportedLocale.ZhTw;
      }
    } catch {
      // fall through to generic resolution
    }
  }

  const { normalizedLocale, languageAlias } = normalizeTrimmedLocaleInput(trimmedLocale);
  if (!normalizedLocale) {
    return undefined;
  }

  if (supportedLocalesSet.has(normalizedLocale)) {
    return withoutRtlLocaleWhenDisabled(
      normalizedLocale as SupportedLocale,
      isRtlLocaleFeatureEnabled
    );
  }

  return withoutRtlLocaleWhenDisabled(
    languageAlias ? localeAliasMap[languageAlias] : undefined,
    isRtlLocaleFeatureEnabled
  );
};

/**
 * Returns `true` when a locale can be mapped to one of the supported locales,
 * including shorthand aliases like `de` or normalized values like `de_DE`.
 */
export const isSupportedLocaleAlias = (
  locale: string | null | undefined,
  options?: MapLocaleToSupportedOptions
): boolean => mapLocaleAliasToSupportedLocale(locale, options) !== undefined;

/**
 * Extracts the leading locale-like path segment from a pathname.
 *
 * Examples:
 * - `/fr-FR/scribe` -> `fr-FR`
 * - `/fr` -> `fr`
 * - `/scribe` -> `undefined`
 */
export const getLocalePathPrefix = (path: string): string | undefined => {
  const match = LOCALE_PATH_PREFIX_REGEX.exec(path);
  return match?.[0].slice(1);
};

/**
 * Returns `true` when a pathname starts with a locale-like prefix such as
 * `/fr`, `/fr-FR`, or `/pt-PT`.
 *
 * This is intentionally broader than `isSupportedLocaleAlias` because callers
 * often need to detect and strip unsupported locale-like prefixes before
 * canonicalizing or redirecting them.
 */
export const hasLocalePathPrefix = (path: string): boolean =>
  getLocalePathPrefix(path) !== undefined;

/**
 * Removes a leading locale-like path segment from a pathname.
 *
 * Examples:
 * - `/fr-FR/scribe` -> `/scribe`
 * - `/fr` -> `/`
 * - `/ar-SA/chat` -> `/chat`
 */
export const stripLocalePathPrefix = (path: string): string =>
  path.replace(LOCALE_PATH_PREFIX_REGEX, '') || '/';

const languageAliasCache = new Map<SupportedLocale, string>();

/**
 * Extracts the language subtag from a supported locale.
 *
 * Example: `SupportedLocale.FrFr` becomes `fr`.
 */
export const getLocaleLanguageAlias = (locale: SupportedLocale): string => {
  const cached = languageAliasCache.get(locale);
  if (cached !== undefined) {
    return cached;
  }
  const alias = new Intl.Locale(locale).language;
  languageAliasCache.set(locale, alias);
  return alias;
};

/**
 * Checks whether the input is already an exact supported locale value.
 *
 * Unlike `isSupportedLocaleAlias`, this does not accept language-only aliases
 * such as `fr`; the input must normalize to the same string and exist in the
 * supported locale set.
 */
export const isSupportedLocale = (
  locale: string | null | undefined,
  options?: MapLocaleToSupportedOptions
): locale is SupportedLocale => {
  const isRtlLocaleFeatureEnabled =
    options?.isRtlLocaleFeatureEnabled ?? DEFAULT_RTL_LOCALE_FEATURE_ENABLED;

  if (!locale) {
    return false;
  }

  const trimmedLocale = trimLocaleInput(locale);
  if (!trimmedLocale) {
    return false;
  }

  if (supportedLocalesSet.has(trimmedLocale) && locale === trimmedLocale) {
    if (
      RTL_SUPPORTED_LOCALES.has(trimmedLocale as SupportedLocale) &&
      !isRtlLocaleFeatureEnabled
    ) {
      return false;
    }
    return true;
  }

  const { normalizedLocale } = normalizeTrimmedLocaleInput(trimmedLocale);
  // `supportedLocalesSet.has(normalizedLocale)` alone would accept case-only
  // variants: `Intl.Locale('EN').toString()` is `en`, which is in the set, but
  // callers must match the canonical supported string exactly (reject `EN`).
  const exactMatch =
    locale === normalizedLocale && supportedLocalesSet.has(normalizedLocale);
  if (
    exactMatch &&
    RTL_SUPPORTED_LOCALES.has(normalizedLocale as SupportedLocale) &&
    !isRtlLocaleFeatureEnabled
  ) {
    return false;
  }
  return exactMatch;
};

/**
 * Maps arbitrary locale input to a supported locale, falling back to the
 * application default when no supported match can be resolved.
 */
export const getSupportedLocaleOrDefault = (
  locale: string | null | undefined,
  options?: MapLocaleToSupportedOptions
): SupportedLocale => mapLocaleAliasToSupportedLocale(locale, options) ?? DEFAULT_LOCALE;
